// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Material
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
// RxJS
import { Observable, Subscription } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
// Services and Models
import {
  Drug,
  DrugActionTypes,
  DrugGroupService,
  DrugOnServerCreated,
  selectDrugLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { skip } from 'rxjs/operators';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-drug-add',
  templateUrl: './drug-add.dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateDrugDialogComponent implements OnInit, OnDestroy {
  drugForm: FormGroup;
  hasFormErrors: boolean;
  clicked: boolean;
  companyId: number;
  filteredGroups: Observable<Array<any>>;
  private subscriptions: Subscription[] = [];
  drugGroupAutocompleteCall = this.drugGroupService.searchDrugGroups.bind(this.drugGroupService);
  @ViewChild('drugGroupAutocomplete') drugGroupAutocomplete: GeneralAutocompleteComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CreateDrugDialogComponent>,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private translate: TranslateService,
    public dialog: MatDialog,
    private drugGroupService: DrugGroupService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.createForm();
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      this.companyId = res !== undefined ? res.companyID : -1;
    });
    this.subscriptions.push(userSub);
    const successSubscription = this.store
      .pipe(select(selectDrugLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          switch (res.action_type) {
            case DrugActionTypes.DrugCreated:
              this.dialogRef.close({ added: true });
              break;
            default:
              break;
          }
        }
      });
    this.subscriptions.push(successSubscription);
  }

  getTitle() {
    return this.translate.instant('DRUG.NEW_DRUG');
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Save data
   */
  onSubmit() {
    this.hasFormErrors = false;
    const controls = this.drugForm.controls;
    this.drugGroupAutocomplete.updateValueAndValidity();

    /** check form */
    if (this.drugForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }
    this.clicked = true;
    this.createDrug(this.prepareDrug());
  }

  /**
   * Returns object for saving
   */
  prepareDrug(): Drug {
    const controls = this.drugForm.controls;
    const _drug = new Drug();
    _drug.drug = controls.drug.value;
    _drug.strength = controls.strength.value;
    _drug.unit = controls.unit.value;
    _drug.ndc = controls.ndc.value;
    _drug.brand_generic_flag = controls.brand_generic_flag.value;
    _drug.brand = controls.brand.value;
    _drug.generic = controls.generic.value;
    _drug.th_class = controls.th_class.value;
    _drug.cfi = controls.cfi.value;
    _drug.drug_schedule = controls.drug_schedule.value;
    _drug.otc_flag = controls.otc_flag.value;
    _drug.upc_code = controls.upc_code.value;
    _drug.processed = controls.processed.value;
    _drug.hicl_seqno = controls.hicl_seqno.value;
    _drug.pack_size = controls.pack_size.value;
    _drug.jcode = controls.jcode.value;
    _drug.gpi = controls.gpi.value;
    _drug.rxcui = controls.rxcui.value;
    _drug.ddid = controls.ddid.value;
    _drug.drug_group = controls.drug_group.value;
    _drug.is_active = true;
    for (const [key, value] of Object.entries(_drug)) {
      if (typeof value === 'string') {
        _drug[key] = value.trim();
      }
    }
    return _drug;
  }

  /**
   * Create drug
   *
   * @param _drug: Drug
   */
  createDrug(_drug: Drug) {
    this.store.dispatch(new DrugOnServerCreated({ drug: _drug }));
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.drugForm.controls[controlName];
    if (!control) {
      return false;
    }
    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  close() {
    this.dialogRef.close();
  }

  createForm() {
    this.drugForm = this.fb.group({
      drug: [null, Validators.required],
      strength: [null],
      unit: [null],
      ndc: [null],
      brand_generic_flag: [false],
      brand: [null],
      generic: [null],
      th_class: [null],
      cfi: [null],
      drug_schedule: [null],
      otc_flag: [false],
      upc_code: [null],
      processed: [null],
      hicl_seqno: [null],
      pack_size: [null],
      jcode: [null],
      gpi: [null],
      rxcui: [null],
      ddid: [null],
      drug_group: [null, Validators.required],
    });
  }

  displayFn(drugGroup): string {
    return drugGroup ? drugGroup.name : '';
  }

  filterDrugGroupFn(event: any): string {
    return event ? event.name : '';
  }

  onDrugGroupChange(val) {
    this.drugForm.controls.drug_group.setValue(val);
    this.drugForm.controls.drug_group.updateValueAndValidity();
  }
}
